import { faClose } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { ReactNode, useEffect, useRef } from "react";
import "./FloatContainer.css";

type FloatContainerProps = {
  className?: string;
  children: ReactNode;
  close: () => void;
};

export function FloatContainer(props: FloatContainerProps) {
  const innerRef = useRef<HTMLDivElement>(null);
  // Keep the latest close handler reachable from the key listener without
  // re-subscribing it on every render.
  const closeRef = useRef(props.close);
  closeRef.current = props.close;

  useEffect(() => {
    // Remember what had focus so we can hand it back when the modal closes.
    // Without this, keyboard users get dropped at the top of the page.
    const previouslyFocused = document.activeElement as HTMLElement | null;
    // Pull focus into the dialog, but only when nothing inside already owns
    // it (an autoFocus field, for instance) so we do not fight the content.
    const inner = innerRef.current;
    if (inner && !inner.contains(document.activeElement)) {
      inner.focus();
    }
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        closeRef.current();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      previouslyFocused?.focus?.();
    };
  }, []);

  return (
    <div className={`floatContainer ${props.className ?? ""}`}>
      <div
        ref={innerRef}
        role="dialog"
        aria-modal="true"
        tabIndex={-1}
        className={`floatContainerInner ${props.className ? props.className + "Inner" : ""}`}
      >
        <button
          type="button"
          aria-label="Fechar"
          onClick={props.close}
          className={`floatContainerClose ${props.className ? props.className + "Close" : ""}`}
        >
          <FontAwesomeIcon icon={faClose} />
        </button>
        {props.children}
      </div>
    </div>
  );
}
